import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { MEGA_MENU } from "@/data/navigation";

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileNav = ({ isOpen, onClose }: MobileNavProps) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const toggle = (i: number) => setExpandedIndex(expandedIndex === i ? null : i);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 bottom-0 w-80 max-w-[85vw] bg-card border-l border-border z-50 overflow-y-auto"
          >
            <div className="flex items-center justify-between p-4 border-b border-border/50">
              <span className="font-display text-sm font-semibold">Menu</span>
              <button onClick={onClose} aria-label="Close navigation menu" className="h-8 w-8 rounded-md flex items-center justify-center hover:bg-accent">
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="p-3 space-y-1">
              <Link
                to="/"
                onClick={onClose}
                className="block px-3 py-2.5 rounded-md text-sm font-medium text-foreground hover:bg-accent/50"
              >
                Home
              </Link>

              <Link
                to="/about"
                onClick={onClose}
                className="block px-3 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent/50"
              >
                About
              </Link>

              <Link
                to="/contact"
                onClick={onClose}
                className="block px-3 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent/50"
              >
                Contact
              </Link>

              {MEGA_MENU.map((section, i) => (
                <div key={section.label}>
                  <button
                    onClick={() => toggle(i)}
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent/50"
                  >
                    {section.label}
                    {expandedIndex === i ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>

                  <AnimatePresence>
                    {expandedIndex === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="pl-3 pb-2 space-y-0.5">
                          {section.groups.map((group) =>
                            group.items.map((item) => (
                              <Link
                                key={item.href}
                                to={item.href}
                                onClick={onClose}
                                className="flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-primary hover:bg-accent/30 transition-colors"
                              >
                                <item.icon className="h-3.5 w-3.5 shrink-0" />
                                {item.label}
                              </Link>
                            ))
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </nav>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MobileNav;
